'use client';

import { Divider, IconButton, Stack, SvgIcon, Typography, useTheme } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';

import { layersConfig } from '../../mapbox-common/config';
import { PlaceCard, PlaceCardNameIcon, PlaceCardRoute } from '../../mapbox-common/styles';
import { PlaceProps, RouteType } from '../../mapbox-common/types';
import { bbox, secondsToTime } from '../../mapbox-common/utils';
import { closeIcon, mapCycling, mapDriving, mapWalking } from '../../../config/icons';
import { useIsMobile } from '../../../hooks';
// import { useLocales } from '@/locales';
import { useMapboxStore, useMapboxStoreActions } from '../store';

const DEFAULT_TIME_STRING = '...';

export default function CurrentPlaceCard({ t }: { t: (key: string) => string }) {
  // const { currentLang } = useLocales();
  const direction = useTheme().direction;
  const isMobile = useIsMobile();
  const { center, centerApi, currentPlaceId, directionsClientRef, mapRef } = useMapboxStore();
  const { setCurrentPlaceId, setCurrentRoute, updatePlace, getPlaceById } = useMapboxStoreActions();

  const [currentPlaceLocal, setCurrentPlaceLocal] = useState<PlaceProps>();

  const [activeRoute, setActiveRoute] = useState<RouteType>();
  const [walkingTime, setWalkingTime] = useState<string>(DEFAULT_TIME_STRING);
  const [drivingTime, setDrivingTime] = useState<string>(DEFAULT_TIME_STRING);
  const [cyclingime, setCyclingime] = useState<string>(DEFAULT_TIME_STRING);

  useEffect(() => {
    setCurrentPlaceLocal(getPlaceById(currentPlaceId));
  }, [currentPlaceId]);

  const currentPlaceCleanup = () => {
    setCurrentPlaceId(undefined);
    setCurrentPlaceLocal(undefined);
    setCurrentRoute(undefined);
    setActiveRoute(undefined);
  };

  useEffect(() => {
    if (currentPlaceLocal) {
      setActiveRoute(undefined);
      if (!currentPlaceLocal.routesLoaded) {
        initPlaceRoutes();
        initRoutesTimes();
      } else {
        initRoutesTimes();
      }
    } else {
      currentPlaceCleanup();
    }
  }, [currentPlaceLocal]);

  const initRoutesTimes = useCallback(async () => {
    if (currentPlaceLocal && currentPlaceLocal?.routesLoaded && currentPlaceLocal?.routes) {
      setCyclingime(secondsToTime(t, currentPlaceLocal.routes.cycling.duration));
      setDrivingTime(secondsToTime(t, currentPlaceLocal.routes.driving.duration));
      setWalkingTime(secondsToTime(t, currentPlaceLocal.routes.walking.duration));
    } else {
      setCyclingime(DEFAULT_TIME_STRING);
      setDrivingTime(DEFAULT_TIME_STRING);
      setWalkingTime(DEFAULT_TIME_STRING);
    }
  }, [currentPlaceLocal]);

  const getRoute = useCallback(
    async (profile: RouteType, waypoints: any[]) => {
      try {
        const response = await directionsClientRef
          .getDirections({ profile, waypoints, geometries: 'geojson' })
          .send();
        return response?.body?.routes[0] || false;
      } catch (error) {
        console.error(`Failed to get ${profile} directions:`, error);
        return DEFAULT_TIME_STRING;
      }
    },
    [directionsClientRef],
  );

  const initPlaceRoutes = useCallback(async () => {
    const origin = centerApi || center?.location;

    if (
      !center ||
      !origin ||
      !directionsClientRef ||
      !currentPlaceLocal ||
      currentPlaceLocal?.routesLoaded
    ) {
      console.log('No routes data found!');
      return;
    }

    const waypoints = [
      { coordinates: [origin.lng, origin.lat] },
      { coordinates: [currentPlaceLocal.lng, currentPlaceLocal.lat] },
    ];

    const cycling = await getRoute(RouteType.Cycling, waypoints);
    const driving = await getRoute(RouteType.Driving, waypoints);
    const walking = await getRoute(RouteType.Walking, waypoints);

    if (!cycling || !driving || !walking) {
      console.log('No routes data found!');
      return;
    }

    // if project has centerApi then store complete route for drawing
    const newCurrentPlace = {
      ...currentPlaceLocal,
      routesLoaded: true,
      routes: {
        cycling: {
          ...cycling,
          geometry: {
            ...cycling.geometry,
            coordinates: centerApi
              ? [[center.location.lng, center.location.lat], ...cycling.geometry.coordinates]
              : cycling.geometry.coordinates,
          },
        },
        driving: {
          ...driving,
          geometry: {
            ...driving.geometry,
            coordinates: centerApi
              ? [[center.location.lng, center.location.lat], ...driving.geometry.coordinates]
              : driving.geometry.coordinates,
          },
        },
        walking: {
          ...walking,
          geometry: {
            ...walking.geometry,
            coordinates: centerApi
              ? [[center.location.lng, center.location.lat], ...walking.geometry.coordinates]
              : walking.geometry.coordinates,
          },
        },
      },
    };
    updatePlace(newCurrentPlace);
    setCurrentPlaceLocal(newCurrentPlace);

    // GET RICH DETAILS
    // const placeData = await fetchFromFoursquare(
    //   `https://api.foursquare.com/v3/places/${p.fsq_id}`,
    //   {
    //     fields: [
    //       'fsq_id',
    //       'name',
    //       'location',
    //       'description',
    //       'photos',
    //       // link,chains,categories,geocodes
    //       'stats',
    //       // distance,email,fax,verified,related_places,timezone,hours,features,tips
    //     ].join(','),
    //   },
    // );
    // GET FIRST PHOTO, BY POPULARITY
    // const photos = await fetchFromFoursquare(
    //   `https://api.foursquare.com/v3/places/${currentPlaceLocal.fsq_id}/photos`,
    //   {
    //     limit: 1, // Changed to 1 since we only need the first photo
    //     sort: 'POPULAR',
    //     classifications: 'outdoor,indoor',
    //   },
    // );
    // if (photos.data?.[0]) {
    //   const photo = photos.data[0];
    //   currentPlaceLocal.photo = photo.prefix + '500x500' + photo.suffix;
    //   updatePlace(currentPlaceLocal);
    // }
  }, [
    currentPlaceLocal,
    setCurrentPlaceLocal,
    updatePlace,
    getRoute,
    setWalkingTime,
    setDrivingTime,
    setCyclingime,
  ]);

  const handlePlaceClose = (
    e: React.MouseEvent<HTMLButtonElement> | React.TouchEvent<HTMLButtonElement>,
  ) => {
    e.stopPropagation();
    currentPlaceCleanup();
  };

  const handleActiveRoute = useCallback(
    async (route: RouteType) => {
      if (!mapRef || !currentPlaceLocal?.routes) return;
      setActiveRoute(route);
      setCurrentRoute(currentPlaceLocal.routes[route].geometry);

      const [minLng, minLat, maxLng, maxLat] = bbox(
        currentPlaceLocal.routes[route].geometry.coordinates,
      );

      mapRef.getMap().fitBounds(
        [
          [minLng, minLat],
          [maxLng, maxLat],
        ],
        {
          padding: {
            top: isMobile ? 270 : 100,
            bottom: isMobile ? 50 : 100,
            left: !isMobile && direction === 'rtl' ? 300 : isMobile ? 20 : 100,
            right: !isMobile && direction === 'ltr' ? 300 : isMobile ? 20 : 100,
          },
          duration: 1000,
        },
      );
    },
    [currentPlaceLocal, directionsClientRef, mapRef],
  );

  if (!currentPlaceLocal) return null;

  return (
    <PlaceCard>
      <IconButton
        onClick={handlePlaceClose}
        onTouchStartCapture={handlePlaceClose}
        sx={{
          position: 'absolute',
          zIndex: '1',
          right: '-20px',
          top: '-20px',
          borderRadius: '50%',
        }}
      >
        <SvgIcon>{closeIcon}</SvgIcon>
      </IconButton>
      {/* {!isMobile && currentPlaceLocal.photo && (
        <Img src={currentPlaceLocal.photo} style={{ maxHeight: '125px' }} />
      )} */}
      <Stack gap="0" direction="column">
        <Stack gap="10px" direction="row" justifyContent="space-between" sx={{ padding: '14px' }}>
          <Stack gap="5px" direction="column">
            <Stack gap="6px" direction="row">
              <Typography color="#5C5C5C" variant="body2" lineHeight="18px">
                {t(`labels.${currentPlaceLocal.layer}`)}
              </Typography>
              <PlaceCardNameIcon>{layersConfig[currentPlaceLocal.layer].icon}</PlaceCardNameIcon>
            </Stack>
            {!!currentPlaceLocal.name && (
              <Typography variant="h6" fontWeight="600" lineHeight="22px">
                {currentPlaceLocal.name}
              </Typography>
            )}
            {!!currentPlaceLocal.address && (
              <Typography variant="body2" lineHeight="18px">
                {currentPlaceLocal.address}
              </Typography>
            )}
          </Stack>
          {/* {isMobile && currentPlaceLocal.photo && (
            <Img src={currentPlaceLocal.photo} width={50} height={50} borderRadius={8} />
          )} */}
        </Stack>

        <Divider style={{ width: '100%' }} />
        <Stack gap="10px" sx={{ padding: '14px' }} direction="row" justifyContent="space-between">
          <PlaceCardRoute
            isActive={activeRoute === RouteType.Driving}
            onClick={() => {
              handleActiveRoute(RouteType.Driving);
            }}
            onTouchStartCapture={() => {
              handleActiveRoute(RouteType.Driving);
            }}
          >
            {mapDriving}
            <Typography
              fontSize="12px"
              sx={{
                textAlign: 'center',
                width: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {drivingTime}
            </Typography>
          </PlaceCardRoute>
          <PlaceCardRoute
            isActive={activeRoute === RouteType.Cycling}
            onClick={() => {
              handleActiveRoute(RouteType.Cycling);
            }}
            onTouchStartCapture={() => {
              handleActiveRoute(RouteType.Cycling);
            }}
          >
            {mapCycling}
            <Typography
              fontSize="12px"
              sx={{
                textAlign: 'center',
                width: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {cyclingime}
            </Typography>
          </PlaceCardRoute>
          <PlaceCardRoute
            isActive={activeRoute === RouteType.Walking}
            onClick={() => {
              handleActiveRoute(RouteType.Walking);
            }}
            onTouchStartCapture={() => {
              handleActiveRoute(RouteType.Walking);
            }}
          >
            {mapWalking}
            <Typography
              fontSize="12px"
              sx={{
                textAlign: 'center',
                width: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {walkingTime}
            </Typography>
          </PlaceCardRoute>
        </Stack>
      </Stack>
    </PlaceCard>
  );
}
